/**
 * The blank class of the HQ blog, in ONE place: the client mirror of the server's
 * `backend/modules/hq_blog/schemas.py` (`trim_edges`, `trim_body`).
 *
 * The class: Unicode whitespace (`str.isspace`) + U+200B/200C/200D/2060/FEFF + the C0
 * controls U+0000-U+001F. `String.prototype.trim` is a DIFFERENT set (it misses U+0085,
 * U+001C-U+001F, U+200B, U+2060 and the other C0 controls), and a regex with leading and
 * trailing runs backtracks quadratically on a long interior blank run, so the ends are
 * found by a plain scan over UTF-16 code units (every member of the class is in the BMP).
 * A table of every character, shared with the backend tests, pins the two sides together.
 */

/** @param {number} unit a UTF-16 code unit. */
export function isBlankClass(unit) {
  return unit <= 0x20
    || unit === 0x85 || unit === 0xa0 || unit === 0x1680
    || (unit >= 0x2000 && unit <= 0x200d)
    || unit === 0x2028 || unit === 0x2029 || unit === 0x202f || unit === 0x205f
    || unit === 0x2060 || unit === 0x3000 || unit === 0xfeff;
}

const firstContent = (s, end) => {
  let i = 0;
  while (i < end && isBlankClass(s.charCodeAt(i))) i += 1;
  return i;
};

const endOfContent = (s) => {
  let end = s.length;
  while (end > 0 && isBlankClass(s.charCodeAt(end - 1))) end -= 1;
  return end;
};

/** The text without the leading and trailing characters of the blank class. */
export function trimEdges(v) {
  const s = String(v ?? "");
  const end = endOfContent(s);
  return s.slice(firstContent(s, end), end);
}

/**
 * The server's rule for a Markdown body (`schemas.trim_body`): leading LINES made only of
 * blank-class characters are removed (a line break is "\n" or "\r"), the first line with
 * content keeps its indentation byte for byte, trailing blank-class characters are removed;
 * a body of blank-class characters only is "".
 */
export function trimBody(v) {
  const s = String(v ?? "");
  const end = endOfContent(s);
  const first = firstContent(s, end);
  if (first === end) return "";
  const start = Math.max(s.lastIndexOf("\n", first - 1), s.lastIndexOf("\r", first - 1)) + 1;
  return s.slice(start, end);
}

/** True when the text holds nothing but blank-class characters (or is not a string). */
export const isBlank = (v) => typeof v !== "string" || firstContent(v, v.length) === v.length;
